import { motion } from 'framer-motion';
import { ShieldCheck, TrendingUp, Check, Download, FileText, ExternalLink } from 'lucide-react';
import { V1_STATS, V2_STATS, ASSETS, COPY, formatNumber } from '../config/orbConfig';

const EngineCard = ({ stats, assets, index }) => {
  const Icon = stats.name === 'V1' ? ShieldCheck : TrendingUp;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="glass rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all"
      style={{ borderColor: `${stats.color}30` }}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div 
          className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${stats.color}20` }}
        >
          <Icon size={24} style={{ color: stats.color }} />
        </div>
        <div>
          <h3 className="font-heading font-bold text-xl text-white">
            {stats.name} · <span style={{ color: stats.color }}>{stats.label}</span>
          </h3>
          <p className="text-white/50 text-sm">{stats.tagline}</p>
        </div>
      </div>
      
      {/* Description */}
      <p className="text-white/60 text-sm mb-6 leading-relaxed">
        {stats.description}
      </p>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <div className="bg-white/5 rounded-xl p-3">
          <div className="text-white/40 text-xs mb-1">Return (1yr)</div>
          <div className="font-heading font-bold text-lg" style={{ color: stats.color }}>
            +{stats.oneYear.totalReturn}%
          </div>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="text-white/40 text-xs mb-1">Win Rate</div>
          <div className="font-heading font-bold text-lg text-white">
            {stats.oneYear.winRate}%
          </div>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="text-white/40 text-xs mb-1">Max Drawdown</div>
          <div className="font-heading font-bold text-lg text-[#F59E0B]">
            {stats.oneYear.maxDrawdown}%
          </div>
        </div>
        <div className="bg-white/5 rounded-xl p-3">
          <div className="text-white/40 text-xs mb-1">Profit Factor</div>
          <div className="font-heading font-bold text-lg text-white">
            {stats.oneYear.profitFactor}
          </div>
        </div>
      </div>
      
      {/* Trade Count */}
      <div className="flex items-center justify-between py-3 border-t border-white/10 mb-6">
        <span className="text-white/50 text-sm">Trades (1 year)</span>
        <span className="text-white font-medium">{stats.oneYear.totalTrades}</span>
      </div>
      <div className="flex items-center justify-between py-3 border-t border-white/10 mb-6">
        <span className="text-white/50 text-sm">Trades (5 years)</span>
        <span className="text-white font-medium">{formatNumber(stats.fiveYear.totalTrades, 0)}</span>
      </div>
      
      {/* Recommended Risk */}
      <div className="bg-white/5 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between">
          <span className="text-white/60 text-sm">Recommended Risk</span>
          <span className="font-heading font-bold text-lg" style={{ color: stats.color }}>
            {stats.recommendedRisk}%
          </span>
        </div>
        <p className="text-white/40 text-xs mt-2">
          For a 4% max drawdown budget
        </p>
      </div>
      
      {/* Download Buttons */}
      <div className="space-y-2">
        <a
          href={assets.pdfReport}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full py-3 rounded-xl border border-white/10 hover:border-white/20 bg-white/5 hover:bg-white/10 text-white text-sm font-medium flex items-center justify-center gap-2 transition-all"
        >
          <FileText size={16} style={{ color: stats.color }} />
          View 1-Year Report
          <ExternalLink size={12} className="text-white/40" />
        </a>
        <a
          href={assets.csv5Year}
          download
          className="w-full py-3 rounded-xl text-white text-sm font-medium flex items-center justify-center gap-2 transition-all"
          style={{ backgroundColor: `${stats.color}20`, borderColor: `${stats.color}40` }}
        >
          <Download size={16} />
          Download 5 Years of Trades DATA
          <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">CSV</span>
        </a>
      </div>
    </motion.div>
  );
};

export const EngineComparison = () => {
  return (
    <section id="engines" className="py-16 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-3">
            {COPY.engines.sectionTitle}
          </h2>
          <p className="text-white/50 text-sm max-w-xl mx-auto">
            {COPY.engines.sectionSubtitle}
          </p>
        </div>
        
        {/* Engine Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          <EngineCard stats={V1_STATS} assets={ASSETS.v1} index={0} />
          <EngineCard stats={V2_STATS} assets={ASSETS.v2} index={1} />
        </div>

      </div>
    </section>
  );
};

export default EngineComparison;
